import { useMemo, useState, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { EmptyState } from "@/components/v2/empty-state";
import { UnresolvedFieldBadge } from "@/components/v2/unresolved-field-badge";
import { FixContactFieldSheet } from "@/components/v2/fix-contact-field-sheet";
import { formatDate } from "@/lib/utils";
import { useContact } from "@/hooks/use-api";
import { useAbility } from "@/hooks/use-ability";
import type { UnresolvedFieldEntry } from "@adva/types";
import { RoleBadge, roleLabel, hostPathForRole } from "./shared";

type Row = Record<string, unknown>;
type FixableField = "email" | "phone";
type FixSheetState =
  | { open: false }
  | { open: true; field: FixableField; entry: UnresolvedFieldEntry | null };

function formatContactType(type: string | null | undefined): string | null {
  if (!type) return null;
  return type.replace(/^./, (c) => c.toUpperCase());
}

// Right rail key/value row — skips render when value is falsy. Mirrors the
// customer detail page's InfoRow helper.
function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

// ADV-1101: dual-display row for a contact-identity field that may have an
// unresolved imported value. Three render branches:
//
//   1. clean value + no unresolved entry → plain text (no badge / no Fix).
//   2. null value + unresolved entry      → raw value + badge + Fix button.
//   3. clean value + unresolved entry     → both. Manual-override edge case
//      that can happen when a rep typed a real value but left the unresolved
//      flag behind. Keep the Fix affordance so they can dismiss the flag.
//
// The plain "—" empty state matches the surrounding `InfoRow` rhythm —
// always rendered (overrides InfoRow's early-return-null) so the layout
// doesn't reflow when a value lands.
function UnresolvedFieldRow({
  label,
  value,
  unresolved,
  canFix,
  onFix,
}: {
  label: string;
  value: string | null | undefined;
  unresolved: UnresolvedFieldEntry | null | undefined;
  canFix: boolean;
  onFix: () => void;
}) {
  const display = value ?? unresolved?.raw_value ?? null;
  return (
    <div className="flex items-start justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <div className="flex flex-col items-end gap-1 min-w-0">
        <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
          {display ?? "—"}
        </span>
        {unresolved ? (
          <div className="flex items-center gap-1.5">
            <UnresolvedFieldBadge reason={unresolved.reason} />
            {canFix ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 px-2 typo-label"
                onClick={onFix}
              >
                Fix {label.toLowerCase()}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function ContactsBreadcrumb({
  label,
  actions,
}: {
  label: string;
  actions?: ReactNode;
}) {
  const crumb = (
    <Breadcrumb className="typo-body">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            href="/contacts"
            className="text-[color:var(--color-text-lo)]"
          >
            Contacts
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
          /
        </BreadcrumbSeparator>
        <BreadcrumbItem>
          <BreadcrumbPage>{label}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
  if (!actions) {
    return <div className="mb-3">{crumb}</div>;
  }
  return (
    <div className="mb-3 flex items-center justify-between">
      {crumb}
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}

export default function V2ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useContact(id!);

  const roles = (data?.contact ?? []) as Row[];
  // All rows share the same contact identity; take the first for the header.
  const contact = roles[0];

  const name = useMemo(() => {
    if (!contact) return `Contact ${id?.slice(0, 8) ?? ""}`;
    const composed = [contact.first_name, contact.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (composed) return composed;
    return (contact.company_name as string) || "Unnamed contact";
  }, [contact, id]);

  // Hooks must come before any conditional returns (react-hooks/rules-of-hooks).
  const ability = useAbility();
  const [fixSheet, setFixSheet] = useState<FixSheetState>({ open: false });

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        role="status"
        aria-label="Loading contact"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (isError || !contact) {
    const errorTitle = isError ? "Failed to load contact" : "Contact not found";
    const errorDescription = isError
      ? "Something went wrong. Please try again."
      : "This contact may have been deleted, or has no role in this business.";
    return (
      <>
        <ContactsBreadcrumb label={id ?? "..."} />
        <EmptyState title={errorTitle} description={errorDescription} />
      </>
    );
  }

  const contactType = formatContactType(contact.contact_type as string | null);
  const email = contact.email as string | null;
  const phone = contact.phone as string | null;
  const company = contact.company_name as string | null;
  const createdAt = contact.role_created_at as string | null;

  // ADV-1101: unresolved-fields capture from the lenient import pre-pass.
  // Absent in pre-ADV-1101 fixtures and pre-migration databases — treat as
  // empty in either case.
  const unresolvedFields =
    (contact.unresolved_fields as
      | Record<string, UnresolvedFieldEntry | undefined>
      | null
      | undefined) ?? null;
  const unresolvedEmail = unresolvedFields?.email ?? null;
  const unresolvedPhone = unresolvedFields?.phone ?? null;

  const canFixContact = ability.can("update", "Contact");

  return (
    <div className="flex flex-col">
      <ContactsBreadcrumb
        label={name}
        actions={<DetailPageActions entityLabel="Contact" />}
      />

      <div className="flex gap-6 pt-2">
        {/* Main column */}
        <div className="flex-1 min-w-0 space-y-5">
          {/* Identity block */}
          <div>
            <h1 className="typo-heading text-[color:var(--color-text-hi)]">
              {name}
            </h1>
            {company != null && company !== name && (
              <p className="mt-0.5 typo-body text-[color:var(--color-text-md)]">
                {company}
              </p>
            )}
          </div>

          {/* Inline properties strip. ADV-1101: render the raw unresolved
              value here too — a rep who scans the header should not have to
              hunt for the warning badge in the right rail. */}
          <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
            {contactType != null && <span>{contactType}</span>}
            {(email != null || unresolvedEmail) && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span className="truncate">
                  {email ?? unresolvedEmail?.raw_value}
                </span>
                {unresolvedEmail && (
                  <UnresolvedFieldBadge reason={unresolvedEmail.reason} />
                )}
              </>
            )}
            {(phone != null || unresolvedPhone) && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span className="tabular-nums">
                  {phone ?? unresolvedPhone?.raw_value}
                </span>
                {unresolvedPhone && (
                  <UnresolvedFieldBadge reason={unresolvedPhone.reason} />
                )}
              </>
            )}
            {createdAt != null && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span>Since {formatDate(createdAt)}</span>
              </>
            )}
          </div>

          {/* Roles section */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                Roles
              </span>
              <span className="typo-label text-[color:var(--color-text-lo)]">
                {roles.length}
              </span>
            </div>
            <div className="rounded-md border border-[color:var(--color-border)] divide-y divide-[color:var(--color-border)]">
              {roles.map((role) => {
                const roleType = role.role_type as string;
                const active = role.is_active as boolean;
                const hostPath = hostPathForRole(
                  roleType,
                  role.contact_id as string,
                );
                const clickable = hostPath !== null;
                return (
                  <button
                    key={role.contact_role_id as string}
                    type="button"
                    disabled={!clickable}
                    onClick={() => hostPath && navigate(hostPath)}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left ${
                      clickable
                        ? "hover:bg-[color:var(--color-elevated)] cursor-pointer"
                        : "cursor-default"
                    }`}
                  >
                    <RoleBadge role={roleType} />
                    <span className="typo-body text-[color:var(--color-text-hi)] truncate">
                      {(role.business_name as string) || "—"}
                    </span>
                    <span
                      className={`ml-auto inline-flex items-center gap-1.5 typo-label ${
                        active
                          ? "text-[color:var(--color-success)]"
                          : "text-[color:var(--color-text-lo)]"
                      }`}
                    >
                      <span
                        className={`size-1.5 rounded-full ${
                          active
                            ? "bg-[color:var(--color-success)]"
                            : "bg-[color:var(--color-text-lo)]"
                        }`}
                      />
                      {active ? "Active" : "Inactive"}
                    </span>
                    {clickable && (
                      <span className="typo-label text-[color:var(--color-accent)]">
                        View {roleLabel(roleType)} →
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right rail */}
        <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
          <RailSection title="Details">
            <UnresolvedFieldRow
              label="Email"
              value={email}
              unresolved={unresolvedEmail}
              canFix={canFixContact}
              onFix={() =>
                setFixSheet({
                  open: true,
                  field: "email",
                  entry: unresolvedEmail,
                })
              }
            />
            <UnresolvedFieldRow
              label="Phone"
              value={phone}
              unresolved={unresolvedPhone}
              canFix={canFixContact}
              onFix={() =>
                setFixSheet({
                  open: true,
                  field: "phone",
                  entry: unresolvedPhone,
                })
              }
            />
            <InfoRow label="Company" value={company} />
            <InfoRow label="Type" value={contactType} />
            <InfoRow label="Roles" value={String(roles.length)} />
            <InfoRow
              label="Created"
              value={createdAt ? formatDate(createdAt) : null}
            />
          </RailSection>
        </div>
      </div>

      {fixSheet.open && (
        <FixContactFieldSheet
          contactId={id!}
          field={fixSheet.field}
          entry={fixSheet.entry}
          open={fixSheet.open}
          onClose={() => setFixSheet({ open: false })}
        />
      )}
    </div>
  );
}
